import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Warning,
  Refresh,
  Science,
  Visibility,
  VisibilityOff,
  ExpandMore,
  Settings,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';
import { LoggerService } from '../services/LoggerService';

interface DataSource {
  id: string;
  name: string;
  type: 'fund' | 'regulatory' | 'market' | 'risk' | 'entity';
  provider: string;
  api_key?: string;
  api_url: string;
  is_enabled: boolean;
  is_configured: boolean;
  last_updated?: string;
  status: 'active' | 'inactive' | 'error' | 'testing';
  description: string;
  rate_limit?: number;
  update_frequency: string;
  data_types: string[];
  test_endpoint?: string;
}

interface DataSourceTest {
  source_id: string;
  status: 'success' | 'error' | 'testing';
  message: string;
  response_time?: number;
  last_tested?: string;
}

const DataSourcesPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<DataSourceTest[]>([]);
  const [showApiKeys, setShowApiKeys] = useState<{ [key: string]: boolean }>({});
  const [editDialog, setEditDialog] = useState<{ open: boolean; source: DataSource | null }>({
    open: false,
    source: null,
  });

  useEffect(() => {
    loadDataSources();
  }, []);

  const loadDataSources = async () => {
    try {
      setLoading(true);
      const response = await api.get('/data-sources');
      setDataSources(response.data);
    } catch (error: any) {
      LoggerService.error('Error loading data sources:', error);
      enqueueSnackbar('Failed to load data sources', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSource = async (sourceId: string, enabled: boolean) => {
    try {
      setSaving(true);
      await api.put(`/data-sources/${sourceId}/toggle`, { enabled });
      
      setDataSources(prev => prev.map(source => 
        source.id === sourceId 
          ? { ...source, is_enabled: enabled, status: enabled ? 'active' : 'inactive' }
          : source
      ));
      
      enqueueSnackbar(`Data source ${enabled ? 'enabled' : 'disabled'}`, { variant: 'success' });
    } catch (error: any) {
      LoggerService.error('Error toggling data source:', error);
      enqueueSnackbar('Failed to update data source', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestSource = async (sourceId: string) => {
    try {
      setTesting(sourceId);
      const response = await api.post(`/data-sources/${sourceId}/test`);
      
      const testResult: DataSourceTest = {
        source_id: sourceId,
        status: response.data.status,
        message: response.data.message,
        response_time: response.data.response_time,
        last_tested: new Date().toISOString(),
      };
      
      setTestResults(prev => {
        const existing = prev.findIndex(r => r.source_id === sourceId);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = testResult;
          return updated;
        }
        return [...prev, testResult];
      });
      
      // Update source status
      setDataSources(prev => prev.map(source => 
        source.id === sourceId 
          ? { ...source, status: testResult.status === 'success' ? 'active' : 'error' }
          : source
      ));
      
      enqueueSnackbar(`Test ${testResult.status}: ${testResult.message}`, { 
        variant: testResult.status === 'success' ? 'success' : 'error' 
      });
    } catch (error: any) {
      LoggerService.error('Error testing data source:', error);
      enqueueSnackbar('Failed to test data source', { variant: 'error' });
    } finally {
      setTesting(null);
    }
  };

  const handleEditSource = (source: DataSource) => {
    setEditDialog({ open: true, source });
  };

  const handleSaveSource = async (source: DataSource) => {
    try {
      setSaving(true);
      await api.put(`/data-sources/${source.id}`, source);
      
      setDataSources(prev => prev.map(s => s.id === source.id ? source : s));
      setEditDialog({ open: false, source: null });
      
      enqueueSnackbar('Data source updated successfully', { variant: 'success' });
    } catch (error: any) {
      LoggerService.error('Error saving data source:', error);
      enqueueSnackbar('Failed to save data source', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle color="success" />;
      case 'error':
        return <Error color="error" />;
      case 'testing':
        return <CircularProgress size={20} />;
      default:
        return <Warning color="warning" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'error':
        return 'error';
      case 'testing':
        return 'info';
      default:
        return 'warning';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'fund':
        return 'primary';
      case 'regulatory':
        return 'secondary';
      case 'market':
        return 'success';
      case 'risk':
        return 'warning';
      case 'entity':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTestResult = (sourceId: string) => {
    return testResults.find(r => r.source_id === sourceId);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Data Sources Configuration
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configure and manage external data sources for real-world financial data integration.
      </Typography>

      <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="Fund Data" />
        <Tab label="Regulatory Data" />
        <Tab label="Market Data" />
        <Tab label="Risk Data" />
        <Tab label="Entity Data" />
      </Tabs>

      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Summary Cards */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {dataSources.filter(s => s.is_enabled).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Sources
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="success.main">
                  {dataSources.filter(s => s.status === 'active').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Healthy Sources
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="error.main">
                  {dataSources.filter(s => s.status === 'error').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Error Sources
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="warning.main">
                  {dataSources.filter(s => !s.is_configured).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unconfigured
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Data Sources Table */}
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="All Data Sources"
                action={
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={loadDataSources}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                }
              />
              <CardContent>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Enabled</TableCell>
                        <TableCell>Last Updated</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dataSources.map((source) => (
                        <TableRow key={source.id}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {source.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {source.provider}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={source.type}
                              size="small"
                              color={getTypeColor(source.type) as any}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              {getStatusIcon(source.status)}
                              <Chip 
                                label={source.status}
                                size="small"
                                color={getStatusColor(source.status) as any}
                                variant="outlined"
                              />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={source.is_enabled}
                                  onChange={(e) => handleToggleSource(source.id, e.target.checked)}
                                  disabled={saving}
                                />
                              }
                              label=""
                            />
                          </TableCell>
                          <TableCell>
                            {source.last_updated ? new Date(source.last_updated).toLocaleDateString() : 'Never'}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={1}>
                              <Tooltip title="Test Connection">
                                <IconButton
                                  size="small"
                                  onClick={() => handleTestSource(source.id)}
                                  disabled={testing === source.id}
                                >
                                  {testing === source.id ? <CircularProgress size={20} /> : <Science />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Configure">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditSource(source)}
                                >
                                  <Settings />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Type-specific tabs */}
      {activeTab > 0 && (
        <Grid container spacing={3}>
          {dataSources
            .filter(source => {
              const typeMap = ['', 'fund', 'regulatory', 'market', 'risk', 'entity'];
              return source.type === typeMap[activeTab];
            })
            .map((source) => (
              <Grid item xs={12} md={6} key={source.id}>
                <Card>
                  <CardHeader
                    title={source.name}
                    subheader={source.provider}
                    action={
                      <Box display="flex" gap={1}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={source.is_enabled}
                              onChange={(e) => handleToggleSource(source.id, e.target.checked)}
                              disabled={saving}
                            />
                          }
                          label=""
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleTestSource(source.id)}
                          disabled={testing === source.id}
                        >
                          {testing === source.id ? <CircularProgress size={20} /> : <Science />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEditSource(source)}
                        >
                          <Settings />
                        </IconButton>
                      </Box>
                    }
                  />
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {source.description}
                    </Typography>
                    
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                      {getStatusIcon(source.status)}
                      <Chip 
                        label={source.status}
                        size="small"
                        color={getStatusColor(source.status) as any}
                        variant="outlined"
                      />
                    </Box>

                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="body2">Configuration Details</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight="medium">
                            API URL:
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {source.api_url}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Update Frequency:
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {source.update_frequency}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Rate Limit:
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {source.rate_limit} requests/hour
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Data Types:
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                            {source.data_types.map((type) => (
                              <Chip key={type} label={type} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </Box>

                        {getTestResult(source.id) && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" fontWeight="medium">
                              Last Test Result:
                            </Typography>
                            <Alert 
                              severity={getTestResult(source.id)?.status === 'success' ? 'success' : 'error'}
                              sx={{ mt: 1 }}
                            >
                              {getTestResult(source.id)?.message}
                              {getTestResult(source.id)?.response_time && (
                                <Typography variant="caption" display="block">
                                  Response time: {getTestResult(source.id)?.response_time}ms
                                </Typography>
                              )}
                            </Alert>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      )}

      {/* Edit Dialog */}
      <Dialog 
        open={editDialog.open} 
        onClose={() => setEditDialog({ open: false, source: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Configure Data Source</DialogTitle>
        <DialogContent>
          {editDialog.source && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="API Key"
                    type={showApiKeys[editDialog.source.id] ? 'text' : 'password'}
                    value={editDialog.source.api_key || ''}
                    onChange={(e) => setEditDialog({
                      ...editDialog,
                      source: { ...editDialog.source!, api_key: e.target.value }
                    })}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowApiKeys(prev => ({
                            ...prev,
                            [editDialog.source!.id]: !prev[editDialog.source!.id]
                          }))}
                        >
                          {showApiKeys[editDialog.source.id] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="API URL"
                    value={editDialog.source.api_url}
                    onChange={(e) => setEditDialog({
                      ...editDialog,
                      source: { ...editDialog.source!, api_url: e.target.value }
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Update Frequency</InputLabel>
                    <Select
                      value={editDialog.source.update_frequency}
                      onChange={(e) => setEditDialog({
                        ...editDialog,
                        source: { ...editDialog.source!, update_frequency: e.target.value }
                      })}
                    >
                      <MenuItem value="real-time">Real-time</MenuItem>
                      <MenuItem value="15 minutes">15 minutes</MenuItem>
                      <MenuItem value="1 hour">1 hour</MenuItem>
                      <MenuItem value="4 hours">4 hours</MenuItem>
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Rate Limit (requests/hour)"
                    type="number"
                    value={editDialog.source.rate_limit || ''}
                    onChange={(e) => setEditDialog({
                      ...editDialog,
                      source: { ...editDialog.source!, rate_limit: parseInt(e.target.value) }
                    })}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, source: null })}>
            Cancel
          </Button>
          <Button 
            onClick={() => editDialog.source && handleSaveSource(editDialog.source)}
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataSourcesPage;
